import Vue from 'vue';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { editHistory } from '@/mixins/history';
import { recycleDroplet } from '@/mixins/recycle-droplet';
import _ from 'lodash';

export default Vue.component('recycle-section', {
  template: require('./template.html'),
  mixins: [guid, recycleDroplet, editHistory],
  computed: {
    ...mapGetters(['currentSite', 'currentPage']),
    sections() {
      const exists = this.currentSite.recycled;
      const list = exists ? _.filter(exists, drp => drp.type === this.droplet.type) : [];
      const sortOn = this.sortKey || this.cols[0];
      return _.chain(list)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                const e = [];
                // only grab the properties that the table uses
                _.filter(row, () => {
                  _.forEach(this.cols, (c) => {
                    // clean up the values so that the
                    // searchable items are like how
                    // the template is written
                    const col = this.cleanItem(row[c], c);
                    e.push(col);
                  });
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .sort((a, b) => {
          // We'll sort accending by default and we'll also ignore case
          // since that may confuse non-technical users
          const ascDesc = this.sortMap[sortOn];
          a = _.isString(a[sortOn]) ? _.toLower(a[sortOn]) : a[sortOn]; // eslint-disable-line
          b = _.isString(b[sortOn]) ? _.toLower(b[sortOn]) : b[sortOn]; // eslint-disable-line
          return (a === b ? 0 : a < b ? 1 : -1) * ascDesc; // eslint-disable-line
        })
        .value();
    },
  },
  props: ['droplet', 'items', 'index', 'open'],
  data() {
    const cols = ['name', 'createdDate'];
    const sortMap = _.zipObject(cols, _.fill(Array(cols.length), -1));
    return {
      cols,
      loading: false,
      sortKey: '',
      filterKey: '',
      sortMap,
      recyclename: '',
      paginate: ['sections'],
      confirmDel: false,
      activeRecycled: null,
      confirmReplace: false,
    };
  },
  name: 'recycle-section',
  watch: {
    open(o) {
      if (o) {
        this.recyclename = '';
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit('close-recycle');
    },
    confirmReplaceDrp(droplet) {
      this.activeRecycled = droplet;
      this.confirmReplace = true;
    },
    async preLoadRecycled(droplet) {
      await this.routeRecycleAction({
        action: 'loadFromRecycled',
        newId: this.droplet._id, // new id has been generated when dropped
        type: this._dropletType(droplet),
        droplet,
      });
      droplet._id = this.droplet._id;
      return droplet;
    },
    async recycleLoadDroplet() {
      this.loading = true;
      const items = this.items;
      const index = this.index;
      try {
        this.$emit('close-recycle');
        await this._timeBuffer(300);
        const droplet = await this.preLoadRecycled(_.cloneDeep(this.activeRecycled));
        this.$store.dispatch('runStateFn', {
          fn() {
            items.splice(index, 1);
          },
        });
        setTimeout(() => {
          this.$store.dispatch('runStateFn', {
            fn() {
              items.splice(index, 0, droplet);
            },
          });
          this.setEditHistory({
            type: 'load_recycled',
            meta: {
              dropletType: this.droplet.type,
            },
          });
        }, 250);
      } catch (e) {
        this.$log.error(e);
      } finally {
        this.loading = false;
        this.confirmReplace = false;
      }
    },
    async preSaveNewRecycled(droplet) {
      const newId = this.guid();
      await this.routeRecycleAction({
        action: 'saveNewRecycled',
        newId,
        type: this._dropletType(droplet),
        droplet,
      });
      droplet.name = this.recyclename;
      droplet._id = newId;
      return droplet;
    },
    async saveSection() {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          this.loading = true;
          const n = {
            id: this.guid(),
            type: 'success',
            message: `Successfully created new droplet: ${this.recyclename}!`,
          };
          try {
            // if site does not have recycled property yet, add it
            if (!this.currentSite.recycled) {
              this.$store.dispatch('setGlobalNewValues', {
                vm: this,
                item: this.currentSite,
                values: [{
                  key: 'recycled',
                  value: [],
                }],
              });
            }
            const droplet = await this.preSaveNewRecycled(_.cloneDeep(this.droplet));
            this.$store.dispatch('setGlobalValue', {
              array: true,
              item: this.currentSite,
              key: 'recycled',
              value: droplet,
            });
            await this._SEND([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'recycled',
                value: this.currentSite.recycled,
              },
            }]);
            this.$log.debug('successfully updated recycled');
            this.$emit('close-recycle');
          } catch (e) {
            this.$log.error(e);
            n.type = 'error';
            n.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'recycleSave',
            });
          } finally {
            this.loading = false;
            this.$store.dispatch('addNotification', n);
          }
        }
      });
    },
    setSortKey(k) {
      this.sortKey = k;
      // Invert the value for ascDesc
      this.sortMap[k] = this.sortMap[k] * -1;
    },
    highlight(ctx, query, col) {
      const c = this.cleanItem(ctx, col);
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
    cleanItem(item, col) {
      let i = item || '--';
      if (col === 'createdDate' && i !== '--') {
        i = this._zoneFormat({
          date: i,
          format: 'MM-DD-YYYY hh:mm A z',
          zone: this.currentSite.timezone,
        });
      }
      return i;
    },
    cancelRecycle() {
      this.closeModal();
      this.recyclename = '';
      this.$refs.paginatedSections.$forceUpdate();
    },
    seperateCamalCase(str, mobile) {
      const tail = mobile ? ': ' : '';
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`) + tail;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/section/components/recycle-modal/index.js